import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import Breadcrumb from "@/components/Breadcrumb";
import { LinkedinLogo, TwitterLogo, EnvelopeSimple, Check } from "@phosphor-icons/react";
import { EDITORIAL_ORG } from "@/config/authors";

export default function AuthorPage() {
  const a = EDITORIAL_ORG;
  return (
    <>
      <SEO
        title="Editorial Team"
        description="Meet the Finance Topia editorial team. Our standards, credentials, and how we produce every article."
        url="/editorial"
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Editorial" }]} />
      </div>

      <header className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 border-b-2 border-foreground pb-8">
        <div className="overline text-primary">The Team</div>
        <h1 className="font-display font-black text-5xl md:text-6xl tracking-tight mt-2">
          {a.name}<span className="italic font-normal">.</span>
        </h1>
        <p className="mt-4 max-w-2xl text-lg text-foreground/70 leading-relaxed">
          {a.short_bio}
        </p>
      </header>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid gap-10 md:grid-cols-12">
        <div className="md:col-span-4">
          <img
            src={a.photo}
            alt={a.photo_alt}
            className="w-full aspect-square object-cover border border-foreground/15"
          />
          <div className="mt-4 flex flex-wrap gap-2">
            {a.linkedin && (
              <a
                href={a.linkedin}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="editor-linkedin"
                className="inline-flex items-center gap-2 border border-foreground/20 h-9 px-3 text-xs hover:bg-foreground hover:text-background transition-colors duration-200"
              >
                <LinkedinLogo size={14} /> LinkedIn
              </a>
            )}
            {a.twitter && (
              <a
                href={a.twitter}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="editor-twitter"
                className="inline-flex items-center gap-2 border border-foreground/20 h-9 px-3 text-xs hover:bg-foreground hover:text-background transition-colors duration-200"
              >
                <TwitterLogo size={14} /> X / Twitter
              </a>
            )}
            <a
              href={`mailto:${a.email}`}
              data-testid="editor-email"
              className="inline-flex items-center gap-2 border border-foreground/20 h-9 px-3 text-xs hover:bg-foreground hover:text-background transition-colors duration-200"
            >
              <EnvelopeSimple size={14} /> Email
            </a>
          </div>
        </div>

        <div className="md:col-span-8">
          <div className="overline mb-3">Bio</div>
          <p className="prose-article">{a.full_bio}</p>

          <div className="overline mb-3 mt-10">Editorial Standards</div>
          <ul className="space-y-2">
            {a.credentials.map((c) => (
              <li key={c} className="flex gap-3 items-start">
                <Check size={16} weight="bold" className="text-primary mt-1 flex-shrink-0" />
                <span>{c}</span>
              </li>
            ))}
          </ul>

          <div className="overline mb-3 mt-10">Our Editorial Process</div>
          <div className="prose-article">
            <p>
              Every article on Finance Topia follows the same pipeline. Editors identify a timely finance
              topic, define an SEO-primary keyword, and draft an outline anchored to the sections beginner
              investors need most: benefits, risks, examples, step-by-step guides, common mistakes, FAQs,
              and a plain-English conclusion.
            </p>
            <p>
              We use large language models (currently Claude Sonnet 4.6) to accelerate the drafting stage.
              The AI works from our editorial prompt template — modeled on CFA Institute standards — and
              produces a structured draft that we review for factual accuracy, cite specific data sources,
              and refine for tone. No article is published without human editorial review of the final
              output.
            </p>
            <p>
              We do not accept sponsored content, do not use affiliate rewrites of external material, and
              do not represent any single broker, fund, or coin. Content on the site is educational only —
              nothing here constitutes personalized investment advice.
            </p>
          </div>

          <div className="mt-10 p-5 border border-foreground/15 bg-muted/40">
            <div className="overline">Transparency Note</div>
            <p className="mt-2 text-sm text-foreground/80 leading-relaxed">
              Finance Topia is an independent publication. We disclose AI-assisted drafting on every article
              via the "AI-assisted research" tag, and provide our full editorial process on this page so
              readers can evaluate our methodology.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
